/**
 * « Va la-bas » : traduire un point du monde en itineraire.
 *
 * POURQUOI CE MODULE EXISTE. Cette decision vivait au milieu de la boucle de
 * jeu alors qu'elle ne touche a rien de ce qui l'entourait: ni au rendu, ni a
 * l'interface, ni au reseau. Elle ne lit que le terrain, la grille de
 * navigation et ce qui est bati — et elle ecrit dans le chemin du joueur. Sortie
 * ici, elle se relit d'un bloc, ce qui compte pour une regle a quatre cas qui
 * s'excluent (nager, sortir de l'eau, sortir d'une maison, y entrer) et dont
 * chacun a deja coute une session de deblocage.
 *
 * Module client et non `shared/`: il JOURNALISE, et le journal est du cote
 * navigateur. Il n'importe pour autant ni le DOM ni three.js.
 */
import type { Batiment } from '../shared/types/monde.ts'
import type { Joueur } from '../shared/types/player.ts'
import type { ITerrain, Vec2 } from '../shared/types/world.ts'

import { GrilleNav, trouverChemin } from '../shared/sim/navigation.ts'
import { allerVers, raisonPositionImpossible, verrouille } from '../shared/sim/joueur.ts'
import { dist } from '../shared/sim/alea.ts'
import {
  batimentContenant,
  batimentEtageContenant,
  dansEmprise,
  dansPiscine,
  pieceAuPoint,
  porteExterieur,
  porteInterieur,
  portePosition,
  seuilPiece,
} from '../shared/sim/batiments.ts'
import { BATIMENTS } from '../shared/data/batiments.ts'
import { NAV_CELL, PLAYER_RADIUS } from '../shared/constants.ts'
import { journal } from './log/journal.ts'

/** Le monde dont l'itineraire a besoin, et rien de plus. */
export interface MondeConduite {
  terrain: ITerrain
  grille: GrilleNav
  batiments: Batiment[]
  /** Identifiant de la maison ou se tient le joueur, sinon null. */
  interieur: string | null
}

/**
 * Conduit le joueur vers une cible, en passant par la porte si la cible est
 * dans une maison.
 *
 * Les maisons ne bloquent pas la grille de navigation (sinon on ne pourrait
 * pas y entrer), donc un A* naif trace un chemin qui traverse les murs: le
 * bonhomme se cognait alors indefiniment contre la paroi, en marchant sur
 * place, sans jamais arriver. On l'amene donc d'abord devant la porte, puis on
 * ajoute deux points a la main pour franchir le seuil.
 */
/**
 * Les etapes qui font passer d'une PIECE a une autre dans le meme batiment —
 * cloisons du rez-de-chaussee ou pieces d'etage selon le niveau — ou null s'il
 * n'y a aucune porte a franchir. Chaque piece n'a qu'une porte, sur sa face
 * -z: on sort par la sienne, on entre par celle de la cible.
 */
function etapesEntrePieces(b: Batiment, de: Vec2, vers: Vec2, niveau: number): Vec2[] | null {
  const depuis = pieceAuPoint(b, de.x, de.z, niveau)
  const arrivee = pieceAuPoint(b, vers.x, vers.z, niveau)
  if (depuis === arrivee) return null
  const points: Vec2[] = []
  if (depuis) {
    const s = seuilPiece(b, depuis)
    points.push(s.dedans, s.devant)
  }
  if (arrivee) {
    const s = seuilPiece(b, arrivee)
    points.push(s.devant, s.dedans)
  }
  return points
}

/** Ajoute au chemin la porte de la piece cloisonnee qui contient la cible. */
function ajouterSeuilCible(chemin: Vec2[], b: Batiment, cible: Vec2): void {
  const piece = pieceAuPoint(b, cible.x, cible.z, 0)
  if (!piece) return
  const s = seuilPiece(b, piece)
  chemin.push(s.devant, s.dedans)
}

/** Au-dela de cette distance du trajet, un batiment ne peut pas le gener. */
const MARGE_CONTOURNEMENT = 30

/**
 * PLANIFIE `plan` EN CONTOURNANT LES BATIMENTS OU L'ON ENTRE.
 *
 * Leurs emprises ne sont pas barrees dans la grille — sinon on n'y entrerait
 * jamais —, si bien qu'un itineraire qui passait A COTE de l'ecole la
 * traversait en ligne droite: le bonhomme entrait par une porte, butait sur le
 * mur d'en face et y restait (vu au telephone le 13 octobre), et la voiture,
 * qui suit le meme chemin, s'arretait contre la facade. Le temps du calcul, on
 * barre donc ceux qui ne sont ni le depart ni l'arrivee, puis on les libere:
 * `bloquerEmprise` compte, donc la grille revient EXACTEMENT a son etat — et
 * rien ne tourne entre les deux, le pas partage ne la voit jamais modifiee.
 */
function enContournant<T>(
  grille: GrilleNav,
  batiments: Batiment[],
  de: Vec2,
  vers: Vec2,
  exclus: readonly (Batiment | null)[],
  plan: () => T,
  toujours: readonly (Batiment | null)[] = [],
): T {
  const x0 = Math.min(de.x, vers.x) - MARGE_CONTOURNEMENT
  const x1 = Math.max(de.x, vers.x) + MARGE_CONTOURNEMENT
  const z0 = Math.min(de.z, vers.z) - MARGE_CONTOURNEMENT
  const z1 = Math.max(de.z, vers.z) + MARGE_CONTOURNEMENT
  const barres: Batiment[] = []
  for (const b of batiments) {
    const def = BATIMENTS[b.kind]
    if (!def.habitable || exclus.some((e) => e?.id === b.id)) continue
    if (!toujours.some((t) => t?.id === b.id)) {
      if (b.pos.x < x0 || b.pos.x > x1 || b.pos.z < z0 || b.pos.z > z1) continue
      // On ne laisse ouvert que le batiment ou l'on EST, ou celui ou l'on va.
      // Un simple rayon autour de son centre laissait aussi ouvert celui qu'on
      // ne faisait que froler: un arbre a un metre de la facade, vise depuis
      // l'arriere-cour, faisait traverser la maison et buter sur le mur du fond
      // (campagne du 13 octobre) — et un buisson colle au mur, vise de derriere,
      // coincait le bonhomme dans l'angle. Un depart ou une arrivee colles au mur
      // tombent dans une case barree: l'A* repart de la case libre voisine, et
      // `conduireVers` rajoute le vrai point au bout.
      if (dansEmprise(b, de.x, de.z) || dansEmprise(b, vers.x, vers.z)) continue
    }
    grille.bloquerEmprise(b.pos, def.taille.l, def.taille.p, b.rot)
    barres.push(b)
  }
  try {
    return plan()
  } finally {
    for (const b of barres) {
      const def = BATIMENTS[b.kind]
      grille.libererEmprise(b.pos, def.taille.l, def.taille.p, b.rot)
    }
  }
}

export function conduireVers(joueur: Joueur, cible: Vec2, monde: MondeConduite): boolean {
  const { terrain, grille, batiments, interieur } = monde

  // A L'ETAGE. La grille de navigation ne connait que le sol: la-haut,
  // l'itineraire se compose a la main, en passant par la porte des pieces du
  // niveau — le bureau, la salle a manger, le salon de lecture. Sans ces
  // etapes, un tap vers le lit d'une piece tracait tout droit dans sa cloison
  // et le bonhomme glissait le long du mur sans jamais arriver.
  if (joueur.niveau > 0) {
    const dessus = batimentEtageContenant(batiments, joueur.pos.x, joueur.pos.z, PLAYER_RADIUS)
    if (dessus) {
      const etapes = etapesEntrePieces(dessus, joueur.pos, cible, joueur.niveau) ?? []
      joueur.chemin = [...etapes, { x: cible.x, z: cible.z }]
      joueur.direction = null
      journal.debug('chemin', 'itineraire d’etage', { points: joueur.chemin.length })
      return true
    }
  }

  // NAGER. La grille de navigation marque l'eau comme bloquee (sinon l'A*
  // tracerait des itineraires maritimes a travers tout le monde), donc viser
  // l'eau ne donnerait qu'un point sur la rive. On rejoint donc la rive la
  // plus proche de la cible par l'A*, puis on ajoute la cible a la main: le
  // bonhomme entre dans l'eau et continue a la nage.
  const dansEau = terrain.estEau(cible.x, cible.z) || dansPiscine(batiments, cible.x, cible.z)
  const jeNage = joueur.nage || dansPiscine(batiments, joueur.pos.x, joueur.pos.z)

  // SORTIR DE L'EAU. Symetrique du cas de la maison: on nage, la cible est a
  // terre. L'A* part d'une case marquee bloquee (l'eau), donc il ne trouve
  // aucun itineraire et on restait a barboter. On rejoint donc d'abord la rive
  // a la nage, puis l'A* prend le relais depuis la terre ferme.
  if (jeNage && !dansEau) {
    const rive =
      grille.caseLibreLaPlusProche(joueur.pos.x, joueur.pos.z, 60) ??
      grille.caseLibreLaPlusProche(cible.x, cible.z, 60)
    if (!rive) {
      joueur.chemin = [{ x: cible.x, z: cible.z }]
      journal.debug('chemin', 'sortie d’eau sans rive connue')
      return true
    }
    const depuisRive = trouverChemin(grille, rive, cible)
    joueur.chemin = [rive, ...depuisRive]
    joueur.direction = null
    journal.debug('chemin', 'sortie de l’eau par la rive', { points: joueur.chemin.length })
    return true
  }

  /*
   * EN BARQUE, SUR L'EAU, VERS L'EAU: TOUT DROIT. A bord, `nage` est faux, donc
   * on tombait dans « rejoindre la rive par l'A* puis entrer dans l'eau »: le
   * premier point du chemin etait sur la RIVE, la barque y fonçait, touchait
   * terre, et le chemin etait abandonne. Une barque ne va jamais a terre.
   */
  if (dansEau && joueur.vehicule !== null && terrain.estEau(joueur.pos.x, joueur.pos.z)) {
    joueur.chemin = [{ x: cible.x, z: cible.z }]
    joueur.direction = null
    journal.debug('chemin', 'barque: cap direct sur l’eau')
    return true
  }

  if (dansEau) {
    if (jeNage) {
      // Deja a l'eau: on y va tout droit, rien ne gene.
      joueur.chemin = [{ x: cible.x, z: cible.z }]
      joueur.direction = null
      journal.debug('chemin', 'nage directe')
      return true
    }
    const rive = grille.caseLibreLaPlusProche(cible.x, cible.z, 40)
    if (rive && allerVers(joueur, rive, grille)) {
      joueur.chemin.push({ x: cible.x, z: cible.z })
      journal.debug('chemin', 'vers l’eau via la rive', { points: joueur.chemin.length })
      return true
    }
    // Pas de rive trouvee: on tente tout droit, quitte a etre arrete.
    joueur.chemin = [{ x: cible.x, z: cible.z }]
    return true
  }

  const maisonCible = batimentContenant(batiments, cible.x, cible.z)
  const maisonIci = interieur === null ? null : batiments.find((b) => b.id === interieur) ?? null

  // Meme batiment: la seule chose qui puisse gener est une piece cloisonnee
  // (les chambres de l'hotel, les enclos du zoo): on passe par sa porte.
  if (maisonIci && maisonCible && maisonIci.id === maisonCible.id) {
    const etapes = etapesEntrePieces(maisonCible, joueur.pos, cible, 0)
    if (!etapes) return allerVers(joueur, cible, grille)
    joueur.chemin = [...etapes, { x: cible.x, z: cible.z }]
    joueur.direction = null
    journal.debug('chemin', `entre les pieces de ${maisonCible.kind}`, { points: joueur.chemin.length })
    return true
  }

  // SORTIR. On est dedans, la cible est ailleurs: franchir le seuil a la main
  // puis laisser l'A* faire le reste depuis le perron. Sans ca, l'A* tracait
  // tout droit a travers le mur, `resoudreMurs` repoussait, et le bonhomme
  // pietinait jusqu'a ce que le filet anti-blocage abandonne le chemin.
  if (maisonIci) {
    const perron = porteExterieur(maisonIci, 2.4)
    // Depuis une piece cloisonnee (une chambre de l'hotel), on franchit
    // d'abord SA porte, sinon le chemin de sortie traversait sa cloison.
    const depuisPiece = pieceAuPoint(maisonIci, joueur.pos.x, joueur.pos.z, 0)
    const prefixe: Vec2[] = []
    if (depuisPiece) {
      const s = seuilPiece(maisonIci, depuisPiece)
      prefixe.push(s.dedans, s.devant)
    }
    const sortie = [...prefixe, porteInterieur(maisonIci), portePosition(maisonIci), perron]
    /*
     * LA SUITE PART DU PERRON. Elle se calculait par `allerVers(joueur,
     * perron)`, c'est-a-dire depuis la position ACTUELLE, dans la maison, et
     * vers le perron: le chemin ressortait, rentrait se replacer au point de
     * depart, ressortait — et s'arretait sur le perron, sans jamais aller la ou
     * l'on avait touche (vu au telephone le 13 octobre, depuis l'ecole).
     */
    const versLaSuite = maisonCible ? porteExterieur(maisonCible) : cible
    // La maison qu'on quitte est la PREMIERE a contourner: depuis le perron,
    // la ligne droite vers l'arriere-cour la retraverse — et celle ou l'on va
    // aussi, on vise sa porte. Le perron peut alors tomber dans une case
    // barree; `trouverChemin` part de la plus proche.
    const suite = enContournant(
      grille,
      batiments,
      perron,
      versLaSuite,
      [],
      () => trouverChemin(grille, perron, versLaSuite),
      [maisonIci, maisonCible],
    )
    joueur.chemin = sortie.concat(suite.slice(1))
    joueur.direction = null
    if (suite.length === 0) {
      // Meme sans chemin au-dela du perron, on sort: mieux vaut etre dehors.
      journal.debug('chemin', `sortie de ${maisonIci.kind} sans suite`)
      return true
    }
    if (maisonCible) {
      joueur.chemin.push(portePosition(maisonCible), porteInterieur(maisonCible))
      ajouterSeuilCible(joueur.chemin, maisonCible, cible)
      joueur.chemin.push({ x: cible.x, z: cible.z })
    }
    journal.debug('chemin', `sortie de ${maisonIci.kind}`, { points: joueur.chemin.length })
    return true
  }

  // ENTRER. On est dehors et la cible est dans une maison: viser la porte —
  // celle du batiment, puis celle de la piece cloisonnee si la cible y est.
  if (maisonCible) {
    const devant = porteExterieur(maisonCible)
    // La maison VISEE se contourne aussi: on rejoint sa porte, pas son mur du
    // fond (vu au telephone le 13 octobre, en arrivant au musee par derriere).
    const versPorte = () => allerVers(joueur, devant, grille)
    if (!enContournant(grille, batiments, joueur.pos, devant, [], versPorte, [maisonCible])) {
      return false
    }
    joueur.chemin.push(portePosition(maisonCible), porteInterieur(maisonCible))
    ajouterSeuilCible(joueur.chemin, maisonCible, cible)
    joueur.chemin.push({ x: cible.x, z: cible.z })
    journal.debug('chemin', `entree dans ${maisonCible.kind}`, { points: joueur.chemin.length })
    return true
  }

  // Dehors, cible dehors — en contournant ce qu'on ne fait que longer.
  if (!enContournant(grille, batiments, joueur.pos, cible, [], () => allerVers(joueur, cible, grille))) return false
  // Une cible au ras d'une facade tombait dans une case barree le temps du
  // calcul: le chemin s'arrete a la case voisine. La grille rendue, la cible
  // redevient libre — on va donc jusqu'a elle (l'arbre a couper, le banc).
  // Seulement a une case pres: un chemin PARTIEL (budget de l'A* epuise)
  // s'arrete loin, et le prolonger en ligne droite serait pire que l'arret.
  const fin = joueur.chemin[joueur.chemin.length - 1]
  const ecart = fin ? dist(fin.x, fin.z, cible.x, cible.z) : 0
  if (fin && ecart > 0.05 && ecart <= 2 * NAV_CELL && grille.estLibreMonde(cible.x, cible.z)) {
    joueur.chemin.push({ x: cible.x, z: cible.z })
  }
  return true
}

/** Un seul message de deblocage par tranche de temps de jeu. */
const DELAI_MESSAGE_DEBLOCAGE = 8_000

/**
 * Le filet de securite: quand le joueur se retrouve dans une case impossible
 * (pose d'un amenagement sur lui, changement de monde), on le remet sur la case
 * libre la plus proche au lieu de le laisser coince.
 *
 * IL NE DEGAGE PLUS LUI-MEME, IL RACONTE. Le sauvetage a demenage dans
 * `debloquer` (shared/sim/joueur.ts), au coeur du pas de simulation que le
 * serveur execute AUSSI: depuis que le deplacement est en autorite serveur, un
 * client qui se degagerait tout seul serait aussitot renvoye dans le trou par
 * un serveur qui ne saurait pas pourquoi il a bouge — le bonhomme ferait la
 * navette entre les deux. Ce qui reste ici est ce que le serveur ne peut pas
 * faire: le dire au joueur, une fois, sans le repeter.
 *
 * C'est une CLASSE et non une fonction parce qu'elle se souvient de son dernier
 * sauvetage: sans cette memoire, elle parlerait a chaque image.
 */
export class FiletAntiBlocage {
  private readonly terrain: ITerrain
  private readonly grille: GrilleNav
  private readonly message: (texte: string) => void
  /** Temps de jeu du dernier deblocage, pour ne pas repeter le message. */
  private dernierDeblocage = -Infinity

  constructor(terrain: ITerrain, grille: GrilleNav, message: (texte: string) => void) {
    this.terrain = terrain
    this.grille = grille
    this.message = message
  }

  /**
   * A appeler quand le pas de simulation vient de degager le joueur.
   * `depuis` est l'endroit impossible, `vers` celui ou il a ete pose.
   *
   * `muet` journalise SANS parler, et c'est le cas du pilote: `debloquer` fait
   * partie du pas partage, donc il tourne aussi a bord, et la position 2D d'un
   * avion survole tout ce que le terrain compte de falaises. Le filet le
   * « degage » a repetition, `majAvion` le rassoit dans son siege a la ligne
   * suivante — rien ne se voit, sauf le message. Une traversee vers le monde 2
   * passe au-dessus de la montagne et en recoltait trois d'affilee: c'est le
   * defaut deja paye a l'inverse, annoncer quelque chose qui n'arrive pas.
   * Le journal, lui, garde tout: c'est la regle du projet.
   */
  signaler(depuis: Vec2, vers: Vec2, tempsJeu: number, muet = false): void {
    const raison = raisonPositionImpossible(this.grille, this.terrain, depuis.x, depuis.z) ?? 'inconnue'
    journal.warn(
      'joueur',
      `debloque de (${depuis.x.toFixed(1)}, ${depuis.z.toFixed(1)}) vers ` +
        `(${vers.x.toFixed(1)}, ${vers.z.toFixed(1)}): ${raison}`,
    )

    /*
     * Un seul message par accalmie. Se faire degager est utile a savoir une fois;
     * l'apprendre soixante fois en quatorze minutes est un defaut en soi, et ca
     * noie le reste des messages. Le journal, lui, garde toutes les lignes: c'est
     * la qu'on compte les recidives.
     */
    if (!muet && tempsJeu - this.dernierDeblocage > DELAI_MESSAGE_DEBLOCAGE) {
      this.message('Tu étais coincé : je t’ai dégagé.')
    }
    this.dernierDeblocage = tempsJeu
  }
}

/**
 * Annule un chemin qui n'avance plus.
 *
 * Filet de securite general: quelle qu'en soit la cause (mur, obstacle pose
 * en cours de route, cible devenue inaccessible), un bonhomme qui marche sans
 * bouger est un bogue visible. Plutot que de le laisser pietiner, on arrete.
 *
 * Classe et non fonction: la surveillance ne veut rien dire sans la position
 * d'il y a six dixiemes de seconde, et cette memoire n'a a etre lisible par
 * personne d'autre.
 */
export class SurveillanceProgres {
  /** Position au dernier controle de progression, pour detecter un blocage. */
  private readonly dernierePos = { x: 0, z: 0 }
  private minuterie = 0
  private readonly abandonner: () => void

  /** `abandonner` est appele quand le chemin est jete: a l'appelant d'en tirer les consequences. */
  constructor(abandonner: () => void) {
    this.abandonner = abandonner
  }

  verifier(joueur: Joueur, dt: number): void {
    // Un bonhomme VERROUILLE (il finit de poser, de manger) n'avance pas, et
    // ce n'est pas un blocage: compter ce temps-la jetait le chemin d'un tap
    // donne juste apres une pose (vu en campagne, ou 0,9 s de pose dure
    // plusieurs secondes d'horloge). On repart de zero quand il est libere.
    if (joueur.chemin.length === 0 || verrouille(joueur)) {
      this.minuterie = 0
      this.dernierePos.x = joueur.pos.x
      this.dernierePos.z = joueur.pos.z
      return
    }
    this.minuterie += dt
    if (this.minuterie < 0.6) return

    const avance = dist(joueur.pos.x, joueur.pos.z, this.dernierePos.x, this.dernierePos.z)
    this.minuterie = 0
    this.dernierePos.x = joueur.pos.x
    this.dernierePos.z = joueur.pos.z
    if (avance > 0.25) return

    journal.warn('chemin', `bloque apres ${avance.toFixed(2)} m en 0.6 s, chemin abandonne`, {
      restant: joueur.chemin.length,
      pos: { x: Math.round(joueur.pos.x * 10) / 10, z: Math.round(joueur.pos.z * 10) / 10 },
    })
    joueur.chemin = []
    this.abandonner()
  }
}
